import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import { newsList, newsTimeRanges, newsTypes } from "@/mocks/news";

const PAGE_SIZE = 4;

const daysForRange: Record<string, number> = {
  近一周: 7,
  近一月: 30,
  近半年: 182,
};

export default function NewsList() {
  const [type, setType] = useState("全部");
  const [range, setRange] = useState("全部时间");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const now = new Date("2026-10-13").getTime();
    let list = newsList.filter((item) => type === "全部" || item.type === type);
    if (range !== "全部时间") {
      const limit = daysForRange[range] * 24 * 60 * 60 * 1000;
      list = list.filter((item) => now - new Date(item.date).getTime() <= limit);
    }
    return [...list].sort((a, b) => b.date.localeCompare(a.date));
  }, [type, range]);

  const visible = filtered.slice(0, visibleCount);

  const resetFilters = () => {
    setType("全部");
    setRange("全部时间");
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <div className="w-full">
      <PageHeader
        breadcrumb={[{ label: "相关资讯" }]}
        eyebrow="资讯中心"
        title="相关资讯"
        description="汇集公司新闻、行业动态、媒体报道与活动公告，帮你及时掌握想要XX 及相关领域的最新动态。"
      />

      <section className="px-4 md:px-8 py-10 md:py-14">
        <div className="mx-auto max-w-6xl flex flex-col gap-8">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm font-medium text-foreground-700">时间范围</span>
              {newsTimeRanges.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setRange(item);
                    setVisibleCount(PAGE_SIZE);
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                    range === item
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
              <span className="mr-1 text-sm font-medium text-foreground-700">新闻类型</span>
              {newsTypes.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setType(item);
                    setVisibleCount(PAGE_SIZE);
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                    type === item
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-600"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              title="没有符合条件的资讯"
              description="换个时间范围或新闻类型试试，也可以直接重置筛选查看全部资讯。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          ) : (
            <div className="flex flex-col gap-4">
              {visible.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 sm:flex-row"
                >
                  <div className="h-44 w-full shrink-0 overflow-hidden rounded-md sm:h-32 sm:w-52">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2.5">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                      <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-900">
                        {item.type}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        {item.date}
                      </span>
                      <span className="hidden sm:inline">{item.source}</span>
                    </div>
                    <h2 className="font-heading text-lg font-semibold leading-snug text-foreground-950 group-hover:text-primary-600">
                      {item.title}
                    </h2>
                    <p className="text-sm leading-relaxed text-foreground-600 line-clamp-2">
                      {item.summary}
                    </p>
                    <div className="mt-auto flex flex-wrap gap-2 pt-1">
                      {item.points.map((point) => (
                        <span
                          key={point}
                          className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600"
                        >
                          {point}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {visibleCount < filtered.length && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
              >
                加载更多
                <i className="ri-arrow-down-line" />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}